import Swal from "sweetalert2";

const escapeHtml = (unsafe) =>
    String(unsafe ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

/**
 * متن‌های پربسامد خطای پاسخ سرور (Node یا Sanctum exchange) که ممکن است
 * برای کاربر مفهوم نباشند را به پیام انسانی و قابل‌فهم تبدیل می‌کند.
 * - «توکن معتبر نیست یا منقضی شده است» معمولا از مسیر exchange-node-token می‌آید
 *   و در عمل وقتی رخ می‌دهد که شماره موبایل کاربر در سامانه ثبت نشده باشد.
 */
function detectLoginErrorKind(rawMessage) {
    const msg = String(rawMessage ?? "").trim();
    if (!msg) return { kind: "unknown" };

    if (
        msg.includes("توکن معتبر نیست") ||
        msg.includes("توکن منقضی") ||
        msg.includes("توکن یافت نشد") ||
        msg.toLowerCase().includes("token") && (msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("not found"))
    ) {
        return { kind: "phone-not-registered", original: msg };
    }
    if (
        msg.includes("رمز") ||
        msg.includes("کلمه عبور") ||
        msg.includes("گذرواژه") ||
        msg.toLowerCase().includes("password") ||
        msg.toLowerCase().includes("credentials")
    ) {
        return { kind: "wrong-password", original: msg };
    }
    if (
        msg.includes("شماره") ||
        msg.includes("کاربر یافت نشد") ||
        msg.includes("کاربری با این") ||
        msg.toLowerCase().includes("user not found") ||
        msg.toLowerCase().includes("phone")
    ) {
        return { kind: "phone-not-registered", original: msg };
    }
    return { kind: "unknown", original: msg };
}

const baseSwalOptions = {
    rtl: true,
    confirmButtonText: "متوجه شدم",
    confirmButtonColor: "#64748b",
    customClass: { popup: "exam-join-dialog-popup" },
};

/**
 * نمایش یک خطای ساده برای فرم لاگین (ظاهر یکپارچه، RTL، تیتر و توضیح).
 * @param {{ title: string, detail?: string, hint?: string, icon?: 'error'|'warning'|'info' }} opts
 */
export function showSimpleLoginErrorSwal({ title, detail, hint, icon = "error" }) {
    const safeDetail = detail ? escapeHtml(detail) : "";
    const safeHint = hint ? escapeHtml(hint) : "";
    const html = `
        <div style="text-align:right;">
            ${
                safeDetail
                    ? `<p style="margin:0 0 12px;font-size:15px;line-height:1.85;color:#334155;">${safeDetail}</p>`
                    : ""
            }
            ${
                safeHint
                    ? `<p style="margin:0;font-size:13px;line-height:1.7;color:#64748b;padding-top:10px;border-top:1px solid #e2e8f0;">${safeHint}</p>`
                    : ""
            }
        </div>
    `;
    return Swal.fire({
        ...baseSwalOptions,
        icon,
        iconColor: icon === "warning" ? "#d97706" : icon === "info" ? "#0ea5e9" : "#dc2626",
        title,
        html,
    });
}

/** والیدیشن: شماره موبایل وارد نشده */
export const showPhoneRequiredSwal = () =>
    showSimpleLoginErrorSwal({
        title: "شماره موبایل را وارد کنید",
        detail: "برای ورود به آزمون، شماره موبایل خود را وارد کنید.",
        icon: "warning",
    });

/** والیدیشن: طول شماره نادرست */
export const showPhoneLengthInvalidSwal = () =>
    showSimpleLoginErrorSwal({
        title: "شماره موبایل نامعتبر است",
        detail: "شماره موبایل باید ۱۱ رقمی و با ۰۹ آغاز شود (مانند ۰۹۱۲۳۴۵۶۷۸۹).",
        icon: "warning",
    });

/** والیدیشن: قالب شماره نادرست */
export const showPhoneFormatInvalidSwal = () =>
    showSimpleLoginErrorSwal({
        title: "قالب شماره موبایل صحیح نیست",
        detail: "لطفاً شماره موبایل ایرانی معتبر وارد کنید (مانند ۰۹۱۲۳۴۵۶۷۸۹).",
        icon: "warning",
    });

/** والیدیشن: رمز عبور وارد نشده */
export const showPasswordRequiredSwal = () =>
    showSimpleLoginErrorSwal({
        title: "رمز عبور را وارد کنید",
        detail: "برای ورود، رمز عبور حساب کاربری خود را وارد کنید.",
        icon: "warning",
    });

/** خطای رمز اشتباه با ظاهر منظم و حرفه‌ای */
export const showWrongPasswordSwal = (originalMessage) =>
    showSimpleLoginErrorSwal({
        title: "رمز عبور وارد شده صحیح نیست",
        detail:
            "شماره موبایل و رمز عبور ثبت‌شده با یکدیگر تطابق ندارند. لطفاً مجدد بررسی کنید و دوباره تلاش نمایید.",
        hint:
            originalMessage && originalMessage.length < 200
                ? `جزئیات: ${originalMessage}`
                : "اگر رمز عبور خود را فراموش کرده‌اید، از گزینهٔ «فراموشی رمز عبور» استفاده کنید.",
        icon: "error",
    });

/** خطای عدم ثبت شماره در آزمون / حساب */
export const showPhoneNotRegisteredSwal = (originalMessage) =>
    showSimpleLoginErrorSwal({
        title: "شماره وارد شده در این آزمون ثبت نشده است",
        detail:
            "شماره موبایلی که وارد کرده‌اید در فهرست شرکت‌کنندگان این آزمون قرار ندارد یا برای آن حساب کاربری فعالی ثبت نشده است. " +
            "اگر باید بتوانید در این آزمون شرکت کنید، لطفاً با برگزارکننده یا آموزشگاه هماهنگ کنید.",
        hint:
            originalMessage && originalMessage.length < 200
                ? `جزئیات فنی: ${originalMessage}`
                : "در صورت فعال بودن ورود مهمان برای این آزمون، می‌توانید از گزینهٔ ورود مهمان استفاده کنید.",
        icon: "warning",
    });

/**
 * برای پاسخ‌های لاگین که از Node API می‌آیند یا نتیجهٔ exchange Sanctum هستند،
 * نوع خطا تشخیص داده شده و مودال متناسب نمایش داده می‌شود.
 *
 * @param {*} response - پاسخ خام (شامل error/message/...)
 * @param {{fallbackTitle?: string, fallbackDetail?: string}} [options]
 */
export function showLoginApiErrorSwal(response, options = {}) {
    const message = response?.error ?? response?.message ?? response?.detail ?? "";
    const detected = detectLoginErrorKind(message);

    if (detected.kind === "wrong-password") {
        return showWrongPasswordSwal(detected.original);
    }
    if (detected.kind === "phone-not-registered") {
        return showPhoneNotRegisteredSwal(detected.original);
    }

    return showSimpleLoginErrorSwal({
        title: options.fallbackTitle || "ورود انجام نشد",
        detail:
            options.fallbackDetail ||
            "در حال حاضر امکان ورود به حساب کاربری وجود ندارد. لطفاً پس از چند لحظه مجدداً تلاش کنید.",
        hint:
            message && message.length < 200
                ? `پیام سرور: ${message}`
                : undefined,
        icon: "error",
    });
}
